import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  type SortDescriptor,
} from '@heroui/react';
import type { Key, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, LoadingState } from './states.js';

/**
 * The product's table.
 *
 * Every list in the system is one of these, so sorting, pagination, the empty
 * message, the loading state and the failure state behave the same everywhere
 * and are impossible to forget.
 *
 * Sorting is SERVER-SIDE by contract: the caller receives a sort descriptor and
 * refetches. Sorting a single page client-side would silently sort only the
 * rows that happen to be loaded, which reads as a whole-dataset ordering and is
 * not one.
 *
 * DIRECTION. Nothing here mirrors columns by hand. The table's own direction
 * comes from the document root, and react-aria takes its keyboard direction
 * from the locale given to HeroUIProvider — so under Arabic the first column is
 * on the right AND the arrow keys move the way the reader expects. Both halves
 * are required; only one of them is CSS.
 *
 * Wide tables scroll inside their own container rather than pushing the page
 * sideways, which on a tablet is the difference between usable and not.
 */

export interface DataColumn<Row> {
  readonly key: string;
  /** Already translated. */
  readonly header: string;
  readonly render: (row: Row) => ReactNode;
  readonly isSortable?: boolean;
  /** Header shown only to assistive technology, for an actions column. */
  readonly isHeaderHidden?: boolean;
  readonly align?: 'start' | 'end';
}

export interface DataTableProps<Row> {
  /** Names the table for assistive technology. Already translated. */
  readonly label: string;
  readonly columns: readonly DataColumn<Row>[];
  readonly rows: readonly Row[];
  readonly rowKey: (row: Row) => Key;

  readonly isLoading?: boolean;
  readonly error?: unknown;
  readonly onRetry?: () => void;

  /** Shown when the request succeeded and matched nothing. Translated. */
  readonly emptyContent?: string;

  readonly sort?: SortDescriptor;
  readonly onSortChange?: (descriptor: SortDescriptor) => void;

  readonly page?: number;
  readonly totalPages?: number;
  readonly onPageChange?: (page: number) => void;
}

export function DataTable<Row>({
  label,
  columns,
  rows,
  rowKey,
  isLoading = false,
  error,
  onRetry,
  emptyContent,
  sort,
  onSortChange,
  page,
  totalPages,
  onPageChange,
}: DataTableProps<Row>) {
  const { t } = useTranslation();

  // A failure replaces the table rather than sitting above stale rows. Leaving
  // the previous results visible next to an error makes them look current.
  if (error) return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  if (isLoading) return <LoadingState />;

  const showPagination = (totalPages ?? 1) > 1 && page && onPageChange;

  return (
    <div className="flex flex-col gap-gutter">
      <div className="overflow-x-auto">
        <Table
          aria-label={label}
          removeWrapper
          /*
            The approved redesign's register (D278): a sunken header row in
            small semibold ink, hairline rules between rows, compact rows and
            a quiet hover — dense enough to scan a day's work, never boxed.
          */
          classNames={{
            th: '!rounded-none h-9 border-b border-default-200 bg-default-50 px-3 text-caption font-semibold text-default-700',
            td: 'border-b border-default-100 px-3 py-2.5 text-body text-default-900 group-data-[last=true]/tr:border-b-0',
            tr: 'transition-colors hover:bg-default-50',
          }}
          {...(sort ? { sortDescriptor: sort } : {})}
          {...(onSortChange ? { onSortChange } : {})}
        >
          <TableHeader>
            {columns.map((column) => (
              <TableColumn
                key={column.key}
                allowsSorting={column.isSortable ?? false}
                align={column.align ?? 'start'}
              >
                {column.isHeaderHidden ? (
                  <span className="sr-only">{column.header}</span>
                ) : (
                  column.header
                )}
              </TableColumn>
            ))}
          </TableHeader>
          <TableBody emptyContent={emptyContent ?? t('common.noResults')}>
            {rows.map((row) => {
              const key = rowKey(row);
              return (
                <TableRow key={key}>
                  {columns.map((column) => (
                    // Keyed by ROW and column together. `key={column.key}`
                    // alone gave every row's cells the same collection key, so
                    // react-stately's own row prefixing (only applied to the
                    // function-children form) never happened and arrow-key
                    // navigation jumped straight to the last row in both
                    // directions (browser check, defect 1).
                    <TableCell key={`${String(key)}:${column.key}`}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {showPagination ? (
        <PaginationNav page={page} totalPages={totalPages ?? 1} onPageChange={onPageChange} />
      ) : null}
    </div>
  );
}

/**
 * The register pager on its own, for a page that lays out several tables over
 * ONE paged list (the sample register grouped by work order, D320).
 */
export function PaginationNav({
  page,
  totalPages,
  onPageChange,
}: {
  readonly page: number;
  readonly totalPages: number;
  readonly onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (totalPages <= 1) return null;
  return (
    <nav aria-label={t('common.pagination')} className="flex justify-center">
      <Pagination
        page={page}
        total={totalPages}
        onChange={onPageChange}
        showControls
        // HeroUI's pagination reads direction from the provider locale, so
        // "next" points the reader's way under Arabic without an override.
        //
        // Its OWN aria-labels are hardcoded English regardless of locale
        // (browser check, defect 5) — `getItemAriaLabel` is the documented
        // escape hatch. Each control is still an ordinary tab stop, not an
        // arrow-key-navigable group: HeroUI's Pagination has no roving
        // tabindex, and adding one is a bigger change than a label fix.
        getItemAriaLabel={(item) => {
          switch (item) {
            case 'prev':
              return t('common.paginationPrevious');
            case 'next':
              return t('common.paginationNext');
            case 'first':
              return t('common.paginationFirst');
            case 'last':
              return t('common.paginationLast');
            case 'dots':
              return t('common.paginationDots');
            default:
              return t('common.paginationPage', { page: item });
          }
        }}
      />
    </nav>
  );
}
